import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery as useTanStackQuery, useQueryClient } from '@tanstack/react-query';
import {
  InfoWindow,
  Marker,
  Polygon,
  Polyline,
} from '@react-google-maps/api';
import {
  Alert,
  Box,
  Button,
  Map,
  Stack,
  TextField,
  Typography,
} from '../../ui/core';
import { cardHeaderSx, filterPillSx, pillButtonSx } from '../../ui/styles';
import ConfirmModal from '../../components/shared/ConfirmModal';
import { useToast } from '../../hooks';
import placeBoundaryApi from '../../lib/api/PlaceBoundaryApi';
import laneMapApi from '../../lib/api/LaneMapApi';
import { geoJsonToPolygonPaths } from './laneGeometry';
import { CIRCLE_SYMBOL_PATH } from '../../ui/core/mapSymbolPaths';

// A path literal rather than `SymbolPath.CIRCLE` (see ui/core/mapSymbolPaths),
// and a frozen constant so every station marker shares one icon identity.
const STATION_ICON = Object.freeze({
  path: CIRCLE_SYMBOL_PATH,
  fillColor: '#FBBC04',
  fillOpacity: 1,
  strokeColor: '#1a1a1a',
  strokeWeight: 1,
  scale: 5,
});

const DEFAULT_CENTER = { lat: 30.2672, lng: -97.7431 };
const DEFAULT_ZOOM = 13;
const MIN_RING_VERTICES = 3;

/**
 * Minimum gap between captured freehand points, in degrees (~10 m).
 *
 * mousemove fires far faster than the boundary needs. Thinning here keeps the
 * payload — and the number of 100-point Roads API chunks the server has to
 * make — proportional to the distance drawn rather than to how slowly the
 * operator moved the mouse.
 */
const TRACE_MIN_STEP_DEGREES = 0.0001;

const farEnough = (a, b) =>
  Math.abs(a.lat - b.lat) > TRACE_MIN_STEP_DEGREES ||
  Math.abs(a.lng - b.lng) > TRACE_MIN_STEP_DEGREES;

const SAVED_STYLE = {
  fillColor: '#4285F4',
  fillOpacity: 0.15,
  strokeColor: '#1a1a1a',
  strokeWeight: 2,
};
const DRAFT_STYLE = {
  fillColor: '#34A853',
  fillOpacity: 0.18,
  strokeColor: '#1a7f37',
  strokeWeight: 2,
};

/**
 * Google's path omits the repeated first vertex; GeoJSON requires it, and
 * ST_GeomFromGeoJSON errors without it.
 */
export const verticesToGeoJson = (vertices) => {
  const ring = vertices.map(({ lng, lat }) => [lng, lat]);
  return { type: 'Polygon', coordinates: [[...ring, ring[0]]] };
};

const PlaceBoundaries = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [map, setMap] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [draftVertices, setDraftVertices] = useState([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);
  const [freehand, setFreehand] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [tracePreview, setTracePreview] = useState([]);

  // Capture buffer for an in-progress drag. Held in a ref, not state: mousemove
  // fires continuously and re-rendering per point would drop events.
  const traceRef = useRef([]);
  const drawingRef = useRef(false);
  // The window-level mouseup handler is registered once per drag, so it must
  // read the CURRENT finish logic rather than the closure it was created with.
  const finishTraceRef = useRef(() => {});

  // The live overlay. Vertex drags mutate this path in place without touching
  // React state, so it — not `draftVertices` — is the truth at save time.
  const draftOverlayRef = useRef(null);

  const { data: boundaries = [] } = useTanStackQuery({
    queryKey: ['place-boundaries'],
    queryFn: () => placeBoundaryApi.fetchPlaceBoundaries(),
  });

  const { data: stationsData } = useTanStackQuery({
    queryKey: ['place-boundary-stations', bounds],
    queryFn: () => laneMapApi.fetchStations(bounds),
    enabled: !!bounds,
  });
  const stations = useMemo(
    () => stationsData?.stations || [],
    [stationsData],
  );

  const onMapLoad = useCallback((instance) => {
    setMap(instance);
  }, []);

  const onIdle = useCallback(() => {
    if (!map?.getBounds) return;
    const b = map.getBounds();
    if (!b) return;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    setBounds({
      swLng: sw.lng(),
      swLat: sw.lat(),
      neLng: ne.lng(),
      neLat: ne.lat(),
    });
  }, [map]);

  // Click-to-place vertices. DrawingManager's drag-to-draw is not available —
  // Google removed it in Maps JS API 3.65 and constructing one throws, taking
  // the page blank (see the LIBRARIES comment in ui/core/Map.js). `editable`
  // on a core Polygon still gives the corner and midpoint handles.
  const onMapClick = useCallback((e) => {
    if (freehand || !e?.latLng) return;
    setDraftVertices((prev) => [
      ...prev,
      { lat: e.latLng.lat(), lng: e.latLng.lng() },
    ]);
  }, [freehand]);

  // Passed only as the initial seed. A fresh array literal each render would
  // re-apply the path from an effect and yank the overlay out from under an
  // in-progress vertex drag.
  const draftPath = useMemo(() => draftVertices, [draftVertices]);

  const onDraftLoad = useCallback((polygon) => {
    draftOverlayRef.current = polygon;
    polygon.addListener('rightclick', (e) => {
      if (e.vertex === undefined) return;
      const path = polygon.getPath();
      if (path.getLength() <= MIN_RING_VERTICES) return;
      path.removeAt(e.vertex);
    });
  }, []);

  const onMouseDown = useCallback(
    (e) => {
      if (!freehand || !e?.latLng) return;
      drawingRef.current = true;
      traceRef.current = [{ lat: e.latLng.lat(), lng: e.latLng.lng() }];
      setTracePreview(traceRef.current);
      // The map pans on drag by default, which is the same gesture as tracing.
      map?.setOptions({ draggable: false });

      // Listen on the WINDOW, not the map. Overlays — including the preview
      // polyline being drawn right under the cursor — sit above the map and
      // swallow its mouseup, and a release outside the map never reaches it at
      // all. Either way the map's own handler silently never fires and capture
      // runs on, appending to the trace as the pointer moves away.
      window.addEventListener('mouseup', () => finishTraceRef.current(), {
        once: true,
      });
    },
    [freehand, map],
  );

  const onMouseMove = useCallback((e) => {
    if (!drawingRef.current || !e?.latLng) return;
    const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    const last = traceRef.current[traceRef.current.length - 1];
    if (last && !farEnough(point, last)) return;
    traceRef.current = [...traceRef.current, point];
    setTracePreview(traceRef.current);
  }, []);

  const onMouseUp = useCallback(async () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    map?.setOptions({ draggable: true });

    const trace = traceRef.current;
    traceRef.current = [];
    setTracePreview([]);
    if (trace.length < MIN_RING_VERTICES) return;

    setSnapping(true);
    setServerError(null);
    try {
      const snapped = await placeBoundaryApi.snapPath(trace);
      // The overlay is rebuilt from the snapped ring, so the ref must not keep
      // pointing at the polygon that was on screen before.
      draftOverlayRef.current = null;
      setDraftVertices(snapped);
    } catch (e) {
      setServerError(e.message);
    } finally {
      setSnapping(false);
    }
  }, [map]);

  finishTraceRef.current = onMouseUp;

  const readDraftRing = useCallback(() => {
    const overlay = draftOverlayRef.current;
    if (!overlay) return draftVertices;
    return overlay
      .getPath()
      .getArray()
      .map((p) => ({ lat: p.lat(), lng: p.lng() }));
  }, [draftVertices]);

  const clearDraft = useCallback(() => {
    draftOverlayRef.current = null;
    traceRef.current = [];
    drawingRef.current = false;
    setTracePreview([]);
    setDraftVertices([]);
    setName('');
    setServerError(null);
  }, []);

  const canSave =
    draftVertices.length >= MIN_RING_VERTICES && name.trim().length > 0 && !saving;

  const onSave = async () => {
    setSaving(true);
    setServerError(null);
    try {
      await placeBoundaryApi.createPlaceBoundary({
        name: name.trim(),
        geometry: verticesToGeoJson(readDraftRing()),
      });
      toast.success(`Saved "${name.trim()}"`);
      clearDraft();
      await queryClient.invalidateQueries({ queryKey: ['place-boundaries'] });
    } catch (e) {
      // Leave the shape and the name on screen. The server's message tells the
      // operator what to correct, which only helps if what they drew survives.
      setServerError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDeactivate = async () => {
    const target = pendingDeactivate;
    setPendingDeactivate(null);
    try {
      await placeBoundaryApi.deactivatePlaceBoundary(target.id);
      toast.success(`Deactivated "${target.name}"`);
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['place-boundaries'] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={cardHeaderSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Boundary name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ width: 240 }}
          />
          <Button sx={pillButtonSx} disabled={!canSave} onClick={onSave}>
            {saving ? 'Saving…' : 'Save boundary'}
          </Button>
          <Button
            sx={pillButtonSx}
            disabled={draftVertices.length === 0 || saving}
            onClick={clearDraft}
          >
            Clear
          </Button>
          <Button
            sx={filterPillSx}
            disabled={saving || snapping}
            onClick={() => setFreehand((on) => !on)}
          >
            {freehand ? 'Mode: trace streets' : 'Mode: place corners'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {snapping
              ? 'Snapping the trace onto the road network…'
              : freehand && draftVertices.length === 0
              ? 'Drag along the streets that bound the place. The trace is snapped to the road network when you release.'
              : draftVertices.length === 0
              ? 'Click the map to place corner points. Satellite view is in the map type control if you need it.'
              : `${draftVertices.length} point${
                  draftVertices.length === 1 ? '' : 's'
                } — drag the handles to adjust, right-click a point to remove it.`}
          </Typography>
        </Stack>
        {serverError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {serverError}
          </Alert>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {/* No `mapTypeId` here, and none set on load: the road map is
            Google's default and the operator's choice of view is theirs to
            keep. Passing it through `options` re-asserts it on every render —
            @react-google-maps/api calls setOptions whenever that object's
            identity changes and ui/core/Map.js rebuilds it each time — so
            zooming (onIdle -> setBounds -> render) snapped the view back. */}
        <Map
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          onIdle={onIdle}
          onClick={onMapClick}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          {stations.map((s) => (
            <Marker
              key={s.id || s.mongo_id}
              position={{ lat: s.latitude, lng: s.longitude }}
              title={s.name}
              icon={STATION_ICON}
            />
          ))}

          {boundaries.map((b) => (
            <Polygon
              key={b.id}
              paths={geoJsonToPolygonPaths(JSON.stringify(b.geometry))}
              // Clickable only outside trace mode: selecting a boundary and
              // ending a drag are the same gesture, and an overlay that takes
              // the mouseup leaves capture stuck on.
              options={{ ...SAVED_STYLE, clickable: !freehand }}
              onClick={() => !freehand && setSelected(b)}
            />
          ))}

          {selected && (
            <InfoWindow
              position={
                geoJsonToPolygonPaths(JSON.stringify(selected.geometry))[0]?.[0]
              }
              onCloseClick={() => setSelected(null)}
            >
              <Stack spacing={1} sx={{ p: 0.5 }}>
                <Typography variant="subtitle2">{selected.name}</Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setPendingDeactivate(selected)}
                >
                  Deactivate
                </Button>
              </Stack>
            </InfoWindow>
          )}

          {tracePreview.length > 1 && (
            <Polyline
              path={tracePreview}
              // Non-clickable so it cannot swallow the mouseup that ends the
              // trace — the cursor is on top of this line by definition.
              options={{
                strokeColor: '#1a7f37',
                strokeWeight: 3,
                clickable: false,
              }}
            />
          )}

          {draftVertices.length >= MIN_RING_VERTICES && (
            <Polygon
              paths={draftPath}
              options={{ ...DRAFT_STYLE, editable: true }}
              onLoad={onDraftLoad}
            />
          )}
          {draftVertices.length > 0 &&
            draftVertices.length < MIN_RING_VERTICES &&
            draftVertices.map((v, i) => (
              // Fewer than 3 points is not yet a polygon; show the corners the
              // operator has placed so far so the map isn't silent.
              // eslint-disable-next-line react/no-array-index-key
              <Marker key={`draft-${i}`} position={v} />
            ))}
        </Map>
      </Box>

      <ConfirmModal
        open={!!pendingDeactivate}
        title="Deactivate boundary"
        message={
          pendingDeactivate
            ? `"${pendingDeactivate.name}" will stop overriding the drive-time contour for new rides. Rides that already have a polygon keep it.`
            : ''
        }
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={onConfirmDeactivate}
        onCancel={() => setPendingDeactivate(null)}
      />
    </Box>
  );
};

export default PlaceBoundaries;
